import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Card, CardContent, CardHeader, CardTitle
} from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, Search, Upload, Users, AlertTriangle, CheckCircle2, RefreshCw, Wand2, Zap, Ruler, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import BuyerItemBulkImport from './BuyerItemBulkImport';
import { useIsAdmin } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyCriteria = {
  category: '',
  diameter_min: '',
  diameter_max: '',
  length_min: '',
  length_max: '',
  brand: '',
  name_contains: '',
};

const emptyForm = { code: '', description: '', active: true, ...emptyCriteria };

const criteriaSummary = (c) => {
  if (!c) return null;
  const parts = [];
  if (c.category) parts.push(c.category);
  const d = [c.diameter_min, c.diameter_max].filter(v => v !== null && v !== undefined && v !== '');
  if (d.length) parts.push(`Ø ${c.diameter_min ?? '…'}–${c.diameter_max ?? '…'} mm`);
  const l = [c.length_min, c.length_max].filter(v => v !== null && v !== undefined && v !== '');
  if (l.length) parts.push(`L ${c.length_min ?? '…'}–${c.length_max ?? '…'} mm`);
  if (c.brand) parts.push(`brand=${c.brand}`);
  if (c.name_contains) parts.push(`name~"${c.name_contains}"`);
  return parts.length ? parts.join(' · ') : null;
};

const toNumOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const BuyerItems = () => {
  const isAdmin = useIsAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Auto-mapping tab state
  const [tab, setTab] = useState('items');
  const [mapRows, setMapRows] = useState([]);
  const [mapSummary, setMapSummary] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [conflictsOnly, setConflictsOnly] = useState(false);
  const [mapSearch, setMapSearch] = useState('');
  const [mapStatusFilter, setMapStatusFilter] = useState('all');

  // Manual conflict-resolution dialog
  const [resolveRow, setResolveRow] = useState(null); // the mapping row being resolved
  const [resolvePick, setResolvePick] = useState('');
  const [resolveSaving, setResolveSaving] = useState(false);

  // Bulk assign unique matches dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignPreview, setAssignPreview] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  // Sync dimensions from size dialog
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncScopeIds, setSyncScopeIds] = useState(null); // null → all; array → subset

  // Chained sync + assign dialog
  const [chainOpen, setChainOpen] = useState(false);
  const [chainPreview, setChainPreview] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [chainResult, setChainResult] = useState(null);

  // Auto-mapping export state
  const [exporting, setExporting] = useState(false);

  const handleExportMappingReport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (conflictsOnly) params.only_conflicts = true;
      if (mapSearch.trim()) params.q = mapSearch.trim();
      if (mapStatusFilter !== 'all') params.status = mapStatusFilter;
      const res = await axios.get(`${API_URL}/buyer-items/auto-mapping-export.csv`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Prefer server-supplied filename (has a timestamp) when it comes through.
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      a.download = match ? match[1] : 'buyer_item_mapping.csv';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      const rows = res.headers?.['x-exported-rows'];
      toast.success(rows ? `Exported ${rows} row(s)` : 'Mapping report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export mapping report');
    } finally { setExporting(false); }
  };

  const notifyBuyerItemsUpdated = () => {
    // Sidebar badge listens for this to refresh its pending-assignment count.
    try { window.dispatchEvent(new Event('buyer-items-updated')); } catch (_) { /* no-op */ }
  };

  const openChainDialog = async () => {
    setChainOpen(true);
    setChainPreview(null);
    setChainResult(null);
    setChainLoading(true);
    try {
      const res = await axios.post(`${API_URL}/buyer-items/sync-and-assign?dry_run=true`);
      setChainPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
      setChainOpen(false);
    } finally { setChainLoading(false); }
  };

  const runChain = async () => {
    setChainLoading(true);
    try {
      const res = await axios.post(`${API_URL}/buyer-items/sync-and-assign`);
      setChainResult(res.data);
      const s = res.data.summary;
      if (s.dimensions_synced + s.unique_matches_assigned > 0) {
        toast.success(`Synced ${s.dimensions_synced} · Assigned ${s.unique_matches_assigned}`);
      } else {
        toast.info('Nothing to sync or assign');
      }
      await fetchAutoMapping();
      notifyBuyerItemsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    } finally { setChainLoading(false); }
  };

  const openSyncDialog = async (productIds = null) => {
    setSyncScopeIds(productIds);
    setSyncOpen(true);
    setSyncPreview(null);
    setSyncResult(null);
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API_URL}/products/sync-dimensions-from-size?dry_run=true`, { product_ids: productIds });
      setSyncPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync preview failed');
      setSyncOpen(false);
    } finally { setSyncLoading(false); }
  };

  const runSync = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API_URL}/products/sync-dimensions-from-size`, { product_ids: syncScopeIds });
      setSyncResult(res.data);
      if (res.data.summary.updated > 0) toast.success(`${res.data.summary.updated} product(s) synced`);
      else toast.info('No products needed a dimension update');
      await fetchAutoMapping();
      notifyBuyerItemsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally { setSyncLoading(false); }
  };

  const openAssignDialog = async () => {
    setAssignOpen(true);
    setAssignPreview(null);
    setAssignResult(null);
    setAssignLoading(true);
    try {
      const res = await axios.post(`${API_URL}/buyer-items/assign-unique-matches?dry_run=true`);
      setAssignPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
      setAssignOpen(false);
    } finally { setAssignLoading(false); }
  };

  const runAssign = async () => {
    setAssignLoading(true);
    try {
      const res = await axios.post(`${API_URL}/buyer-items/assign-unique-matches`);
      setAssignResult(res.data);
      const s = res.data.summary;
      if (s.assigned > 0) toast.success(`${s.assigned} product(s) assigned`);
      else toast.info('No new assignments — everything already resolved');
      await fetchAutoMapping();
      notifyBuyerItemsUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment failed');
    } finally { setAssignLoading(false); }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter === 'active') params.active = true;
      if (statusFilter === 'inactive') params.active = false;
      const res = await axios.get(`${API_URL}/buyer-items`, { params });
      setItems(res.data || []);
    } catch (_e) {
      toast.error('Failed to load Buyer Items');
    } finally { setLoading(false); }
  };

  const fetchAutoMapping = async () => {
    setMapLoading(true);
    try {
      const params = {};
      if (conflictsOnly) params.only_conflicts = true;
      const res = await axios.get(`${API_URL}/buyer-items/auto-mapping-preview`, { params });
      setMapRows(res.data.rows || []);
      setMapSummary(res.data.summary || null);
    } catch (_e) {
      toast.error('Failed to load auto-mapping preview');
    } finally { setMapLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search, statusFilter]);
  useEffect(() => {
    if (tab === 'mapping') fetchAutoMapping();
    /* eslint-disable-next-line */
  }, [tab, conflictsOnly]);

  // Client-side filter for the Auto-Mapping table so the operator can narrow
  // large catalogues by product name / brand / size / buyer item code without
  // waiting for a server round-trip. The underlying rows and match summary are
  // NOT modified — only what is rendered.
  const filteredMapRows = useMemo(() => {
    const needle = mapSearch.trim().toLowerCase();
    return mapRows.filter(r => {
      if (mapStatusFilter !== 'all' && r.status !== mapStatusFilter) return false;
      if (!needle) return true;
      const parts = [
        r.product_name || '',
        r.brand || '',
        r.size || '',
        r.manual_buyer_item_code || '',
        ...(r.matches || []).flatMap(m => [m.code || '', m.description || '']),
      ];
      return parts.join(' ').toLowerCase().includes(needle);
    });
  }, [mapRows, mapSearch, mapStatusFilter]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    const c = it.match_criteria || {};
    setForm({
      code: it.code,
      description: it.description,
      active: !!it.active,
      category: c.category || '',
      diameter_min: c.diameter_min ?? '',
      diameter_max: c.diameter_max ?? '',
      length_min: c.length_min ?? '',
      length_max: c.length_max ?? '',
      brand: c.brand || '',
      name_contains: c.name_contains || '',
    });
    setDialogOpen(true);
  };

  const buildCriteriaPayload = () => {
    const c = {
      category: (form.category || '').trim() || null,
      diameter_min: toNumOrNull(form.diameter_min),
      diameter_max: toNumOrNull(form.diameter_max),
      length_min: toNumOrNull(form.length_min),
      length_max: toNumOrNull(form.length_max),
      brand: (form.brand || '').trim() || null,
      name_contains: (form.name_contains || '').trim() || null,
    };
    const hasAny = Object.values(c).some(v => v !== null && v !== '' && v !== undefined);
    return hasAny ? c : null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.description.trim()) {
      toast.error('Code and description are required');
      return;
    }
    // Range sanity — swap? we just reject illegal ranges.
    const dmin = toNumOrNull(form.diameter_min);
    const dmax = toNumOrNull(form.diameter_max);
    if (dmin !== null && dmax !== null && dmin > dmax) {
      toast.error('Diameter min must be ≤ max'); return;
    }
    const lmin = toNumOrNull(form.length_min);
    const lmax = toNumOrNull(form.length_max);
    if (lmin !== null && lmax !== null && lmin > lmax) {
      toast.error('Length min must be ≤ max'); return;
    }

    const criteria = buildCriteriaPayload();
    setSaving(true);
    try {
      const body = {
        code: form.code,
        description: form.description,
        active: form.active,
        match_criteria: criteria, // null clears
      };
      if (editing) {
        await axios.put(`${API_URL}/buyer-items/${editing.id}`, body);
        toast.success('Buyer Item updated');
      } else {
        await axios.post(`${API_URL}/buyer-items`, body);
        toast.success('Buyer Item added');
      }
      setDialogOpen(false);
      await fetchItems();
      if (tab === 'mapping') fetchAutoMapping();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const toggleActive = async (it) => {
    try {
      await axios.put(`${API_URL}/buyer-items/${it.id}`, { active: !it.active });
      toast.success(it.active ? 'Deactivated' : 'Activated');
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/buyer-items/${deleteTarget.id}`);
      toast.success('Buyer Item deleted');
      setDeleteTarget(null);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
            <Users className="w-6 h-6 text-teal-600" /> Buyer Item Master
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage buyer-facing codes, descriptions and Product → Buyer Item classification rules.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bi-bulk-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Bulk Import
            </Button>
            <Button onClick={openAdd} className="bg-teal-600 hover:bg-teal-700" data-testid="bi-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Buyer Item
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="bi-tabs">
          <TabsTrigger value="items" data-testid="bi-tab-items">Buyer Items</TabsTrigger>
          <TabsTrigger value="mapping" data-testid="bi-tab-mapping" title="Auto-suggests which Buyer Item best matches each Product using existing classification rules — this is NOT supplier SKU mapping">Product → Buyer Item Classification</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" /> Search & Filter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Input placeholder="Search by code, description, brand, category or name rule…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bi-search-input" />
                </div>
                <div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="bi-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="active">Active only</SelectItem>
                      <SelectItem value="inactive">Inactive only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[20%]">Buyer Item Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[26%]">Match Rules</TableHead>
                    <TableHead className="w-[100px] text-center">Status</TableHead>
                    {isAdmin && <TableHead className="w-[160px] text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-sm text-slate-500 py-8">Loading…</TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={isAdmin ? 5 : 4} className="text-center text-sm text-slate-500 py-8">
                      No buyer items yet. Click <span className="font-medium">Add Buyer Item</span> or <span className="font-medium">Bulk Import</span> to get started.
                    </TableCell></TableRow>
                  ) : (
                    items.map(it => (
                      <TableRow key={it.id} data-testid={`bi-row-${it.id}`}>
                        <TableCell className="tabular-nums font-medium">{it.code}</TableCell>
                        <TableCell className="text-slate-700">{it.description}</TableCell>
                        <TableCell>
                          {criteriaSummary(it.match_criteria) ? (
                            <span className="text-xs text-slate-600">{criteriaSummary(it.match_criteria)}</span>
                          ) : (
                            <span className="text-xs text-slate-400">Manual only</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {it.active ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <div className="inline-flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(it)} title="Edit" data-testid={`bi-edit-${it.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => toggleActive(it)} data-testid={`bi-toggle-${it.id}`}>
                                {it.active ? 'Deactivate' : 'Activate'}
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(it)} title="Delete" data-testid={`bi-delete-${it.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-600" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mapping" className="space-y-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-medium">Product → Buyer Item Auto-Mapping</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Products are matched against the criteria of every active Buyer Item. Conflicts are never auto-resolved — narrow the criteria to fix them.</p>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openSyncDialog(null)}
                    data-testid="bi-sync-dims-btn"
                    title="Backfill Diameter/Length from the Size string for products where they are empty"
                  >
                    <Ruler className="w-4 h-4 mr-2" /> Sync Dimensions
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={openAssignDialog}
                    data-testid="bi-assign-unique-btn"
                  >
                    <Zap className="w-4 h-4 mr-2" /> Assign Unique Matches
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700"
                    onClick={openChainDialog}
                    data-testid="bi-sync-and-assign-btn"
                    title="Sync missing dimensions from size, then immediately assign every product that has exactly one active match"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> Sync &amp; Assign
                  </Button>
                )}
                <label className="flex items-center gap-2 text-xs">
                  <Switch checked={conflictsOnly} onCheckedChange={setConflictsOnly} data-testid="bi-mapping-conflicts-only" />
                  Conflicts only
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportMappingReport}
                  disabled={exporting}
                  data-testid="bi-mapping-export-btn"
                  title="Download a CSV of every product with its live Buyer Item mapping, source and status"
                >
                  <Download className="w-4 h-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
                </Button>
                <Button variant="outline" size="sm" onClick={fetchAutoMapping} data-testid="bi-mapping-refresh">
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>
            </CardHeader>
            {mapSummary && (
              <CardContent className="pt-0 space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Total: {mapSummary.total_products}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Unique: {mapSummary.unique_matches}</Badge>
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Conflicts: {mapSummary.conflicts}</Badge>
                  <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Unmapped: {mapSummary.unmapped}</Badge>
                  {(mapSearch.trim() || mapStatusFilter !== 'all') && (
                    <Badge variant="outline" className="border-teal-300 text-teal-700" data-testid="bi-mapping-filter-count">
                      Showing {filteredMapRows.length} of {mapRows.length}
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Filter table by product name, brand, size or buyer item code…"
                      value={mapSearch}
                      onChange={(e) => setMapSearch(e.target.value)}
                      className="pl-9"
                      data-testid="bi-mapping-search-input"
                    />
                  </div>
                  <div>
                    <Select value={mapStatusFilter} onValueChange={setMapStatusFilter}>
                      <SelectTrigger data-testid="bi-mapping-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="unique">Unique matches</SelectItem>
                        <SelectItem value="conflict">Conflicts</SelectItem>
                        <SelectItem value="none">Unmapped</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="w-[110px]">Size</TableHead>
                    <TableHead className="w-[130px]">Ø · L (mm)</TableHead>
                    <TableHead>Matched Buyer Item(s)</TableHead>
                    <TableHead className="w-[120px] text-center">Status</TableHead>
                    <TableHead className="w-[110px] text-right">Resolve</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mapLoading ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">Computing matches…</TableCell></TableRow>
                  ) : filteredMapRows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">
                      {mapRows.length === 0
                        ? (conflictsOnly ? 'No conflicts. Nice!' : 'No products found. Add products or configure Buyer Item match rules.')
                        : 'No rows match your filter. Clear the search or status filter to see more.'}
                    </TableCell></TableRow>
                  ) : (
                    filteredMapRows.map(r => (
                      <TableRow key={r.product_id} data-testid={`bi-map-row-${r.product_id}`} className={cn(r.status === 'conflict' && 'bg-rose-50/60')}>
                        <TableCell>
                          <div className="font-medium">{r.product_name}</div>
                          <div className="text-[11px] text-slate-500">
                            {r.brand}
                            {r.manual_buyer_item_code
                              ? <> · <span className="text-emerald-700 font-medium">manual: {r.manual_buyer_item_code}</span></>
                              : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">{r.size || '—'}</TableCell>
                        <TableCell className="text-xs tabular-nums">
                          {(r.effective_diameter != null && r.effective_length != null) ? (
                            <div>
                              <div>{r.effective_diameter} · {r.effective_length}</div>
                              <div className="text-[10px] text-slate-500">
                                {r.dimensions_source === 'explicit' && <span className="text-teal-700">explicit</span>}
                                {r.dimensions_source === 'mixed' && <span className="text-amber-700">explicit+size</span>}
                                {r.dimensions_source === 'size' && <span>from size</span>}
                                {!r.dimensions_source && r.parsed_diameter != null && <span>from size</span>}
                              </div>
                            </div>
                          ) : (r.parsed_diameter != null && r.parsed_length != null) ? (
                            <div>
                              <div>{r.parsed_diameter} · {r.parsed_length}</div>
                              <div className="text-[10px] text-slate-500">from size</div>
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell>
                          {r.matches.length === 0 ? (
                            <span className="text-xs text-slate-400">No match</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {r.matches.map(m => (
                                <Badge key={m.id} variant="outline" className={cn('font-normal', r.manual_buyer_item_code === m.code && 'border-emerald-400 bg-emerald-50')}>
                                  <span className="font-medium tabular-nums mr-1">{m.code}</span>
                                  <span className="text-slate-600">{m.description}</span>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.status === 'unique' && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" />Unique</Badge>}
                          {r.status === 'conflict' && <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><AlertTriangle className="w-3 h-3 mr-1" />Conflict × {r.match_count}</Badge>}
                          {r.status === 'none' && <Badge variant="secondary" className="bg-slate-100 text-slate-600">Unmapped</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          {isAdmin && r.matches.length > 0 ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setResolveRow(r); setResolvePick(r.manual_buyer_item_code || (r.matches[0]?.code || '')); }}
                              data-testid={`bi-resolve-${r.product_id}`}
                            >
                              <Wand2 className="w-3.5 h-3.5 mr-1" /> Resolve
                            </Button>
                          ) : isAdmin && r.manual_buyer_item_code ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setResolveRow(r); setResolvePick(''); }}
                              data-testid={`bi-clear-${r.product_id}`}
                              title="Clear manual override"
                            >
                              Clear
                            </Button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Buyer Item' : 'Add Buyer Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="bi-code">Buyer Item Code *</Label>
                <Input id="bi-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g., DEN-IMP-002" required data-testid="bi-code-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bi-desc">Buyer Item Description *</Label>
                <Input id="bi-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Implant - Narrow - Medium" required data-testid="bi-desc-input" />
              </div>
            </div>

            <div className="rounded-md border p-3 space-y-3 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Product Classification Criteria</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Optional. When set, any product matching ALL rules below will auto-resolve to this Buyer Item. Leave everything blank for manual-only assignment.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="bi-category" className="text-xs">Category</Label>
                  <Select value={form.category || 'none'} onValueChange={(v) => setForm({ ...form, category: v === 'none' ? '' : v })}>
                    <SelectTrigger id="bi-category" data-testid="bi-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      <SelectItem value="implant">Implant (requires parseable size)</SelectItem>
                      <SelectItem value="generic">Generic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bi-brand" className="text-xs">Brand (exact match)</Label>
                  <Input id="bi-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Optional — e.g., Nobel" data-testid="bi-brand" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Diameter min (mm)</Label>
                  <Input type="number" step="0.1" value={form.diameter_min} onChange={(e) => setForm({ ...form, diameter_min: e.target.value })} placeholder="e.g., 3.0" data-testid="bi-dia-min" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Diameter max (mm)</Label>
                  <Input type="number" step="0.1" value={form.diameter_max} onChange={(e) => setForm({ ...form, diameter_max: e.target.value })} placeholder="e.g., 3.9" data-testid="bi-dia-max" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Length min (mm)</Label>
                  <Input type="number" step="0.1" value={form.length_min} onChange={(e) => setForm({ ...form, length_min: e.target.value })} placeholder="e.g., 10.0" data-testid="bi-len-min" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Length max (mm)</Label>
                  <Input type="number" step="0.1" value={form.length_max} onChange={(e) => setForm({ ...form, length_max: e.target.value })} placeholder="e.g., 12.0" data-testid="bi-len-max" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bi-name-contains" className="text-xs">Product name contains (optional)</Label>
                <Input id="bi-name-contains" value={form.name_contains} onChange={(e) => setForm({ ...form, name_contains: e.target.value })} placeholder="e.g., abutment" data-testid="bi-name-contains" />
              </div>
              <p className="text-[11px] text-slate-500">
                Ranges are inclusive. Example: <span className="font-medium">Narrow / Medium implant</span> → Ø 3.0–3.9 mm, L 10.0–12.0 mm.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm font-medium">Active</Label>
                <p className="text-xs text-slate-500">{form.active ? 'Item is active' : 'Item is inactive'}</p>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="bi-active-switch" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={saving} data-testid="bi-save-btn">
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Buyer Item?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This removes <span className="font-medium">{deleteTarget?.code}</span> permanently. Consider deactivating instead.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BuyerItemBulkImport open={bulkOpen} onOpenChange={setBulkOpen} onImported={fetchItems} />

      {/* Chained Sync + Assign dialog */}
      <Dialog open={chainOpen} onOpenChange={(o) => { if (!o) { setChainOpen(false); setChainPreview(null); setChainResult(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="bi-chain-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-600" /> Sync &amp; Assign Unique Matches</DialogTitle>
          </DialogHeader>

          {chainLoading && !chainPreview && (
            <div className="py-8 text-center text-sm text-slate-500">Analysing products…</div>
          )}

          {chainPreview && !chainResult && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Two safe steps in one action: (1) fill in missing Diameter/Length from the Size string, (2) assign every product that now has exactly one active Buyer Item match. Manual entries are never overwritten.
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-amber-50/60" data-testid="bi-chain-preview-sync">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{chainPreview.summary.dimensions_eligible}</div>
                  <div className="text-xs text-amber-700">Dimensions to sync</div>
                </div>
                <div className="rounded-md border p-3 bg-emerald-50/60" data-testid="bi-chain-preview-assign">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{chainPreview.summary.unique_matches_to_assign}</div>
                  <div className="text-xs text-emerald-700">Unique matches to assign</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{chainPreview.summary.already_mapped}</div>
                  <div className="text-xs text-slate-600">Already mapped</div>
                </div>
                <div className="rounded-md border p-3 bg-rose-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-rose-700">{chainPreview.summary.conflicts}</div>
                  <div className="text-xs text-rose-700">Conflicts (untouched)</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50 col-span-2">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{chainPreview.summary.unmapped}</div>
                  <div className="text-xs text-slate-600">Unmapped after sync</div>
                </div>
              </div>
              {chainPreview.summary.dimensions_eligible === 0 && chainPreview.summary.unique_matches_to_assign === 0 && (
                <p className="text-xs text-slate-500 italic">Nothing to do — every product is already synced &amp; every unique match is already assigned.</p>
              )}
            </div>
          )}

          {chainResult && (
            <div className="space-y-3" data-testid="bi-chain-result">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-amber-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{chainResult.summary.dimensions_synced}</div>
                  <div className="text-xs text-amber-700">Dimensions synced</div>
                </div>
                <div className="rounded-md border p-3 bg-emerald-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{chainResult.summary.unique_matches_assigned}</div>
                  <div className="text-xs text-emerald-700">Unique matches assigned</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{chainResult.summary.already_mapped}</div>
                  <div className="text-xs text-slate-600">Already mapped (kept)</div>
                </div>
                <div className="rounded-md border p-3 bg-rose-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-rose-700">{chainResult.summary.conflicts}</div>
                  <div className="text-xs text-rose-700">Conflicts (untouched)</div>
                </div>
              </div>
              {(chainResult.summary.sync_failed + chainResult.summary.assign_failed) > 0 && (
                <div className="rounded-md border p-3 bg-rose-50/40 text-xs">
                  <div className="text-rose-700">Sync failures: {chainResult.summary.sync_failed} · Assign failures: {chainResult.summary.assign_failed}</div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setChainOpen(false); setChainPreview(null); setChainResult(null); }}>Close</Button>
            {chainPreview && !chainResult && (
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={!chainPreview || chainLoading || (chainPreview.summary.dimensions_eligible === 0 && chainPreview.summary.unique_matches_to_assign === 0)}
                onClick={runChain}
                data-testid="bi-chain-confirm"
              >
                {chainLoading ? 'Working…' : `Sync ${chainPreview.summary.dimensions_eligible} & Assign ${chainPreview.summary.unique_matches_to_assign}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Dimensions from Size dialog */}
      <Dialog open={syncOpen} onOpenChange={(o) => { if (!o) { setSyncOpen(false); setSyncPreview(null); setSyncResult(null); setSyncScopeIds(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="bi-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ruler className="w-5 h-5 text-teal-600" /> Sync Dimensions from Size</DialogTitle>
          </DialogHeader>

          {syncLoading && !syncPreview && (
            <div className="py-8 text-center text-sm text-slate-500">Analysing product sizes…</div>
          )}

          {syncPreview && !syncResult && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Reads each product's Size string and backfills the missing Diameter/Length numeric fields. <span className="font-medium">Manually entered values are never overwritten.</span> Sizes with less than two numbers are skipped.
              </p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-emerald-50/60" data-testid="bi-sync-preview-eligible">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{syncPreview.summary.eligible}</div>
                  <div className="text-xs text-emerald-700">Will be updated</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{syncPreview.summary.already_explicit}</div>
                  <div className="text-xs text-slate-600">Already explicit (kept)</div>
                </div>
                <div className="rounded-md border p-3 bg-amber-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{syncPreview.summary.unparseable}</div>
                  <div className="text-xs text-amber-700">Size unparseable</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{syncPreview.summary.total_products}</div>
                  <div className="text-xs text-slate-600">Products scanned</div>
                </div>
              </div>
              {syncPreview.eligible.length > 0 && (
                <div className="rounded-md border max-h-[240px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="w-[110px]">Size</TableHead>
                        <TableHead className="w-[120px]">Will Set</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncPreview.eligible.slice(0, 100).map(r => (
                        <TableRow key={r.product_id}>
                          <TableCell className="text-xs">
                            <div className="font-medium">{r.product_name}</div>
                            <div className="text-[11px] text-slate-500">{r.brand}</div>
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{r.size || '—'}</TableCell>
                          <TableCell className="text-xs tabular-nums">
                            {r.will_set.diameter_mm != null && <div>Ø {r.will_set.diameter_mm}</div>}
                            {r.will_set.length_mm != null && <div>L {r.will_set.length_mm}</div>}
                          </TableCell>
                        </TableRow>
                      ))}
                      {syncPreview.eligible.length > 100 && (
                        <TableRow><TableCell colSpan={3} className="text-center text-xs text-slate-500">… and {syncPreview.eligible.length - 100} more</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
              {syncPreview.summary.eligible === 0 && (
                <p className="text-xs text-slate-500 italic">Nothing to sync — every product either already has explicit dimensions or has a size string that cannot be reliably parsed.</p>
              )}
            </div>
          )}

          {syncResult && (
            <div className="space-y-3" data-testid="bi-sync-result">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-emerald-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{syncResult.summary.updated}</div>
                  <div className="text-xs text-emerald-700">Updated</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{syncResult.summary.already_explicit}</div>
                  <div className="text-xs text-slate-600">Already explicit (kept)</div>
                </div>
                <div className="rounded-md border p-3 bg-amber-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{syncResult.summary.unparseable}</div>
                  <div className="text-xs text-amber-700">Unparseable (skipped)</div>
                </div>
                <div className="rounded-md border p-3 bg-rose-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-rose-700">{syncResult.summary.failed}</div>
                  <div className="text-xs text-rose-700">Failed</div>
                </div>
              </div>
              {syncResult.failed && syncResult.failed.length > 0 && (
                <div className="rounded-md border p-3 bg-rose-50/40 text-xs">
                  <ul className="list-disc pl-4 text-rose-700">
                    {syncResult.failed.slice(0, 10).map(f => (
                      <li key={f.product_id}><span className="font-medium">{f.product_name}</span>: {f.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSyncOpen(false); setSyncPreview(null); setSyncResult(null); setSyncScopeIds(null); }}>Close</Button>
            {syncPreview && !syncResult && (
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!syncPreview || syncPreview.summary.eligible === 0 || syncLoading}
                onClick={runSync}
                data-testid="bi-sync-confirm"
              >
                {syncLoading ? 'Syncing…' : `Sync ${syncPreview.summary.eligible} product(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Unique Matches dialog */}      <Dialog open={assignOpen} onOpenChange={(o) => { if (!o) { setAssignOpen(false); setAssignPreview(null); setAssignResult(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="bi-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-teal-600" /> Assign Unique Buyer Item Matches</DialogTitle>
          </DialogHeader>

          {assignLoading && !assignPreview && (
            <div className="py-8 text-center text-sm text-slate-500">Computing eligible products…</div>
          )}

          {assignPreview && !assignResult && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">This will save the resolved Buyer Item Code onto every product that has <span className="font-medium">exactly one</span> active match and <span className="font-medium">no</span> manual override. Conflicts and unmapped products are never touched.</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-emerald-50/60" data-testid="bi-assign-preview-eligible">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{assignPreview.summary.eligible}</div>
                  <div className="text-xs text-emerald-700">Will be assigned</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{assignPreview.summary.already_manual}</div>
                  <div className="text-xs text-slate-600">Already mapped (skipped)</div>
                </div>
                <div className="rounded-md border p-3 bg-rose-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-rose-700">{assignPreview.summary.conflicts}</div>
                  <div className="text-xs text-rose-700">Conflicts (skipped)</div>
                </div>
                <div className="rounded-md border p-3 bg-amber-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{assignPreview.summary.unmapped}</div>
                  <div className="text-xs text-amber-700">Unmapped</div>
                </div>
              </div>
              {assignPreview.summary.eligible === 0 && (
                <p className="text-xs text-slate-500 italic">Nothing to assign — every product is already mapped, in conflict, or unmapped.</p>
              )}
              {assignPreview.eligible.length > 0 && (
                <div className="rounded-md border max-h-[220px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="w-[110px]">Size</TableHead>
                        <TableHead className="w-[130px]">Buyer Item</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignPreview.eligible.slice(0, 100).map(r => (
                        <TableRow key={r.product_id}>
                          <TableCell className="text-xs">
                            <div className="font-medium">{r.product_name}</div>
                            <div className="text-[11px] text-slate-500">{r.brand}</div>
                          </TableCell>
                          <TableCell className="text-xs text-slate-600">{r.size || '—'}</TableCell>
                          <TableCell className="text-xs tabular-nums font-medium">{r.chosen_code}</TableCell>
                        </TableRow>
                      ))}
                      {assignPreview.eligible.length > 100 && (
                        <TableRow><TableCell colSpan={3} className="text-center text-xs text-slate-500">… and {assignPreview.eligible.length - 100} more</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {assignResult && (
            <div className="space-y-3" data-testid="bi-assign-result">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-3 bg-emerald-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-emerald-700">{assignResult.summary.assigned}</div>
                  <div className="text-xs text-emerald-700">Assigned</div>
                </div>
                <div className="rounded-md border p-3 bg-slate-50">
                  <div className="text-2xl font-semibold tabular-nums text-slate-700">{assignResult.summary.already_manual}</div>
                  <div className="text-xs text-slate-600">Already mapped (skipped)</div>
                </div>
                <div className="rounded-md border p-3 bg-rose-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-rose-700">{assignResult.summary.conflicts}</div>
                  <div className="text-xs text-rose-700">Conflicts (skipped)</div>
                </div>
                <div className="rounded-md border p-3 bg-amber-50/60">
                  <div className="text-2xl font-semibold tabular-nums text-amber-700">{assignResult.summary.unmapped}</div>
                  <div className="text-xs text-amber-700">Unmapped</div>
                </div>
              </div>
              {assignResult.failed && assignResult.failed.length > 0 && (
                <div className="rounded-md border p-3 bg-rose-50/40 text-xs">
                  <div className="font-medium text-rose-700 mb-1">{assignResult.failed.length} failed</div>
                  <ul className="list-disc pl-4 text-rose-700">
                    {assignResult.failed.slice(0, 10).map(f => (
                      <li key={f.product_id}><span className="font-medium">{f.product_name}</span>: {f.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAssignOpen(false); setAssignPreview(null); setAssignResult(null); }}>Close</Button>
            {assignPreview && !assignResult && (
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!assignPreview || assignPreview.summary.eligible === 0 || assignLoading}
                onClick={runAssign}
                data-testid="bi-assign-confirm"
              >
                {assignLoading ? 'Assigning…' : `Assign ${assignPreview.summary.eligible} product(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve mapping dialog */}
      <Dialog open={!!resolveRow} onOpenChange={(o) => { if (!o) { setResolveRow(null); setResolvePick(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="bi-resolve-dialog">
          <DialogHeader>
            <DialogTitle>Resolve Buyer Item mapping</DialogTitle>
          </DialogHeader>
          {resolveRow && (
            <div className="space-y-3">
              <div className="text-sm">
                <div className="font-medium">{resolveRow.product_name}</div>
                <div className="text-xs text-slate-500">{resolveRow.brand} · {resolveRow.size || '—'}</div>
                {resolveRow.manual_buyer_item_code && (
                  <div className="text-xs text-emerald-700 mt-1">Current manual code: <span className="font-medium">{resolveRow.manual_buyer_item_code}</span></div>
                )}
              </div>
              {resolveRow.matches.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Pick one Buyer Item to save on this product:</Label>
                  <div className="space-y-1 max-h-[260px] overflow-y-auto rounded-md border p-1">
                    {resolveRow.matches.map(m => (
                      <label key={m.id} className={cn('flex items-start gap-2 rounded-md p-2 cursor-pointer hover:bg-slate-50', resolvePick === m.code && 'bg-teal-50')}>
                        <input type="radio" name="bi-resolve-pick" value={m.code}
                          checked={resolvePick === m.code}
                          onChange={() => setResolvePick(m.code)}
                          data-testid={`bi-resolve-pick-${m.code}`}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium">{m.code}</div>
                          <div className="text-xs text-slate-600">{m.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-500">No matching Buyer Items. You can only clear the manual override.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={async () => {
                if (!resolveRow) return;
                setResolveSaving(true);
                try {
                  await axios.patch(`${API_URL}/products/${resolveRow.product_id}/buyer-item-code`, { buyer_item_code: null });
                  toast.success('Manual override cleared');
                  setResolveRow(null); setResolvePick('');
                  await fetchAutoMapping();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed');
                } finally { setResolveSaving(false); }
              }}
              disabled={!resolveRow || resolveSaving || !resolveRow?.manual_buyer_item_code}
              data-testid="bi-resolve-clear"
            >
              Clear override
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setResolveRow(null); setResolvePick(''); }}>Cancel</Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!resolvePick || resolveSaving}
                onClick={async () => {
                  if (!resolveRow || !resolvePick) return;
                  setResolveSaving(true);
                  try {
                    await axios.patch(`${API_URL}/products/${resolveRow.product_id}/buyer-item-code`, { buyer_item_code: resolvePick });
                    toast.success(`Saved ${resolvePick} on product`);
                    setResolveRow(null); setResolvePick('');
                    await fetchAutoMapping();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed');
                  } finally { setResolveSaving(false); }
                }}
                data-testid="bi-resolve-save"
              >
                {resolveSaving ? 'Saving…' : 'Save selection'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerItems;
